/**
 * Mirror Fidelity dashboard (M3.4) — READ-ONLY.
 *
 * "How faithful is each member's Mirror, and is it getting better?" This is the
 * sibling of CoverageDashboardPage: coverage answers *how much have we asked*,
 * fidelity answers *how well does the result sound like them*. It deliberately
 * reuses that page's visual language (DataGrid overview → row click → detail
 * with LinearProgress dimension bars) so the two read as one product.
 *
 * Top: a DataGrid (GET /fidelity/summary) of every member with any Mirror
 * signal — readiness, completeness, convergence, 30-day likeness (with its
 * rater count, because an average of one rating is not an average), Mirror
 * conversation activity, and the persona version actually serving them.
 * Sorted neediest-first by default.
 *
 * Above everything: the pending learned weight-candidate panel — the one thing
 * on this screen that asks the operator for a decision (and it only navigates
 * to the Weights page — no mutation happens here).
 *
 * Row click → detail (GET /fidelity/:memberId): per-dimension coverage bars, the
 * likeness trend split by rater population, evidence by channel, persona version
 * history, and convergence status.
 *
 * Every number is computed by quill-engine and merely displayed here.
 *
 * Ported from quill-app/quill-admin's FidelityDashboardPage.tsx. Redux
 * (fidelitySlice) replaced with local useState/useEffect calling
 * quillApi.fidelity.* directly. Read-only page — no mutations exist in the
 * source (the "Review in Weights" button only navigates), so no ActionConfirm
 * is needed here.
 */

import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Stack,
  LinearProgress,
  Paper,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AutoAwesome as FidelityIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import EmptyState from '../../common/EmptyState';
import {
  quillApi,
  DimensionCoverage,
  FidelitySummaryRow,
  MemberFidelity,
  PendingWeightCandidate,
} from '../../../services/quillAdminService';
import { useQuillHealth } from '../QuillHealthContext';
import { HelpTip } from '../../help/HelpTip';
import LikenessTrendChart from './LikenessTrendChart';
import PendingWeightCandidatePanel from './PendingWeightCandidatePanel';

function pct(n: number | null | undefined): string {
  return n == null ? '—' : `${Math.round(n * 100)}%`;
}

const LABEL_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  rich: 'success',
  developing: 'warning',
  'needs more stories': 'error',
};

// Same bar as CoverageDashboardPage's — the two dashboards share a vocabulary.
const DimensionCoverageBar: React.FC<{ dim: DimensionCoverage }> = ({ dim }) => {
  const value = Math.round(dim.coverage * 100);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {dim.display_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {value}% · {dim.label}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        color={LABEL_COLOR[dim.label] || 'primary'}
        sx={{ height: 10, borderRadius: 5 }}
      />
    </Box>
  );
};

const ConvergedChip: React.FC<{ converged: boolean | null | undefined }> = ({ converged }) => {
  if (converged == null) {
    return <Chip label="unknown" size="small" variant="outlined" />;
  }
  return (
    <Chip
      label={converged ? 'converged' : 'in progress'}
      size="small"
      color={converged ? 'success' : 'default'}
      variant={converged ? 'filled' : 'outlined'}
    />
  );
};

function rationaleValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * compile_rationale is arbitrary JSON emitted by the persona compiler. Rather
 * than dump a raw JSON.stringify() blob into a table cell, render an object as
 * a collapsible key/value list, an array as a comma list, and a primitive as
 * plain text. Keeps the common case (a handful of provenance keys) legible
 * without hiding anything.
 */
const CompileRationale: React.FC<{ rationale: unknown }> = ({ rationale }) => {
  const [open, setOpen] = React.useState(false);

  if (rationale == null || rationale === '') {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  const isPlainObject =
    typeof rationale === 'object' && rationale !== null && !Array.isArray(rationale);
  const entries = isPlainObject ? Object.entries(rationale as Record<string, unknown>) : [];

  if (isPlainObject && entries.length > 0) {
    const summary = entries
      .slice(0, 2)
      .map(([k]) => k)
      .join(', ');
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" noWrap sx={{ flexGrow: 1 }}>
            {entries.length} field{entries.length === 1 ? '' : 's'}: {summary}
            {entries.length > 2 ? '…' : ''}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Hide compile rationale' : 'Show compile rationale'}
          >
            {open ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
          </IconButton>
        </Box>
        <Collapse in={open}>
          <Box component="dl" sx={{ m: 0, mt: 0.5 }}>
            {entries.map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', gap: 1, py: 0.25 }}>
                <Typography component="dt" variant="caption" sx={{ fontWeight: 700, minWidth: 96 }}>
                  {k}
                </Typography>
                <Typography
                  component="dd"
                  variant="caption"
                  sx={{ m: 0, wordBreak: 'break-word', color: 'text.secondary' }}
                >
                  {rationaleValue(v)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  const text = Array.isArray(rationale)
    ? (rationale as unknown[]).map((v) => rationaleValue(v)).join(', ')
    : String(rationale);
  return (
    <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
      {text}
    </Typography>
  );
};

const MemberFidelityDetail: React.FC<{
  memberFidelity: MemberFidelity | null;
  loading: boolean;
  onBack: () => void;
}> = ({ memberFidelity, loading, onBack }) => {
  if (loading && !memberFidelity) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!memberFidelity) return null;

  const conv = memberFidelity.convergence;
  const channels = memberFidelity.evidence_by_channel;
  const maxChannel = channels.reduce((m, c) => Math.max(m, c.count), 0) || 1;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Back to all members
      </Button>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Mirror fidelity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
        {memberFidelity.member_id}
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Convergence
        </Typography>
        {conv ? (
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="center">
            <ConvergedChip converged={conv.converged} />
            <Typography variant="body2">
              Completeness <strong>{pct(conv.completeness)}</strong> (bar: 85%)
            </Typography>
            <Typography variant="body2">
              Unresolved charter-sensitive questions:{' '}
              <strong>
                {conv.unresolvedSensitiveCount === -1 ? 'unknown' : conv.unresolvedSensitiveCount}
              </strong>
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Convergence could not be evaluated (engine unavailable).
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Likeness trend by rater
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Humans (member, family) and machines (Mirror critic, nightly harness) rate the same
          Mirror. Divergence between the two groups is the signal worth chasing.
        </Typography>
        <LikenessTrendChart
          series={memberFidelity.likeness_trend?.series}
          bucket={memberFidelity.likeness_trend?.bucket}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Coverage by dimension
        </Typography>
        {memberFidelity.dimensions.length === 0 ? (
          <EmptyState
            icon="no_data"
            title="No dimension coverage yet"
            description="No scored interview dimensions for this member yet."
          />
        ) : (
          memberFidelity.dimensions.map((d) => <DimensionCoverageBar key={d.dimension_key} dim={d} />)
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Evidence by channel
        </Typography>
        {channels.length === 0 ? (
          <EmptyState
            icon="inbox"
            title="No persona evidence yet"
            description="No evidence has been recorded across any channel for this member."
          />
        ) : (
          <Stack spacing={1.25}>
            {channels.map((c) => (
              <Box key={c.channel} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" sx={{ width: 170, fontWeight: 600 }} noWrap>
                  {c.channel}
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round((c.count / maxChannel) * 100)}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ width: 40, textAlign: 'right' }}>
                  {c.count}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Persona version history
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {memberFidelity.persona_history.length === 0 ? (
          <EmptyState
            icon="history"
            title="No persona compiled yet"
            description="No Mirror persona has been compiled for this member yet."
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Weight version</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Compiled</TableCell>
                <TableCell>Rationale</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberFidelity.persona_history.map((p, i) => (
                <TableRow key={`${p.version}-${i}`}>
                  <TableCell>{p.version ?? '—'}</TableCell>
                  <TableCell>{p.weight_version ?? '—'}</TableCell>
                  <TableCell>
                    {p.is_active ? <Chip label="active" size="small" color="success" /> : '—'}
                  </TableCell>
                  <TableCell>
                    {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <CompileRationale rationale={p.compile_rationale} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

const FidelityDashboardPage: React.FC = () => {
  const { status } = useQuillHealth();

  const [summary, setSummary] = React.useState<FidelitySummaryRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [memberFidelity, setMemberFidelity] = React.useState<MemberFidelity | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [pendingCandidate, setPendingCandidate] = React.useState<PendingWeightCandidate | null>(null);
  const [pendingLoading, setPendingLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<string | null>(null);

  const loadSummary = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await quillApi.fidelity.getSummary());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load fidelity summary.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPendingCandidate = React.useCallback(async () => {
    setPendingLoading(true);
    try {
      setPendingCandidate(await quillApi.fidelity.getPendingWeightCandidate());
    } catch {
      // A missing/failed candidate probe must never blank the dashboard — the
      // panel simply does not render (same fail-soft posture as the backend).
      setPendingCandidate(null);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadSummary();
    loadPendingCandidate();
  }, [loadSummary, loadPendingCandidate]);

  const openMember = async (memberId: string) => {
    setSelected(memberId);
    setDetailLoading(true);
    setError(null);
    try {
      setMemberFidelity(await quillApi.fidelity.getMemberFidelity(memberId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load member fidelity.');
    } finally {
      setDetailLoading(false);
    }
  };
  const back = () => {
    setSelected(null);
    setMemberFidelity(null);
  };

  // Recover from a fetch error in-context (no full page reload): re-run the open
  // member detail if one is selected, else the summary + pending-candidate probe.
  const retry = () => {
    if (selected) {
      openMember(selected);
    } else {
      loadSummary();
      loadPendingCandidate();
    }
  };

  const columns: GridColDef<FidelitySummaryRow>[] = [
    { field: 'member_id', headerName: 'Member', flex: 1, minWidth: 260 },
    {
      field: 'readiness',
      headerName: 'Readiness',
      width: 170,
      type: 'number',
      renderCell: (params: GridRenderCellParams<FidelitySummaryRow, number | null>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={params.value == null ? 0 : Math.round(params.value * 100)}
            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ width: 36, textAlign: 'right' }}>
            {pct(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'completeness',
      headerName: 'Completeness',
      width: 130,
      type: 'number',
      valueFormatter: (value) => pct(value as number | null),
    },
    {
      field: 'converged',
      headerName: 'Converged',
      width: 130,
      sortable: true,
      renderCell: (params: GridRenderCellParams<FidelitySummaryRow, boolean | null>) => (
        <ConvergedChip converged={params.value} />
      ),
    },
    {
      field: 'likeness_avg_30d',
      headerName: 'Likeness 30d',
      width: 130,
      type: 'number',
      renderCell: (params: GridRenderCellParams<FidelitySummaryRow, number | null>) => (
        <Typography variant="body2">
          {params.value == null ? '—' : params.value.toFixed(2)}{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            (n={params.row.likeness_n_30d})
          </Typography>
        </Typography>
      ),
    },
    {
      field: 'mirror_messages',
      headerName: 'Mirror activity',
      width: 140,
      type: 'number',
      renderCell: (params: GridRenderCellParams<FidelitySummaryRow, number>) => (
        <Typography variant="body2">
          {params.row.mirror_conversations} conv · {params.value} msg
        </Typography>
      ),
    },
    { field: 'facts_count', headerName: 'Facts', width: 80, type: 'number' },
    { field: 'evidence_count', headerName: 'Evidence', width: 95, type: 'number' },
    {
      field: 'persona_version',
      headerName: 'Persona',
      width: 110,
      type: 'number',
      renderCell: (params: GridRenderCellParams<FidelitySummaryRow, number | null>) => (
        <Typography variant="body2">
          {params.value == null ? '—' : `v${params.value}`}
          {params.row.persona_weight_version != null && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              / w{params.row.persona_weight_version}
            </Typography>
          )}
        </Typography>
      ),
    },
    {
      field: 'last_activity',
      headerName: 'Last activity',
      width: 180,
      valueFormatter: (value) => (value ? new Date(String(value)).toLocaleString() : '—'),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <FidelityIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Mirror Fidelity Dashboard
        </Typography>
        <HelpTip id="quill-fidelity" />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Readiness, likeness trends, evidence, and persona compile provenance per member.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Box sx={{ flexGrow: 1 }} />
        {!selected && (
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadSummary();
              loadPendingCandidate();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <PendingWeightCandidatePanel candidate={pendingCandidate} loading={pendingLoading} />

      {selected ? (
        <MemberFidelityDetail memberFidelity={memberFidelity} loading={detailLoading} onBack={back} />
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every member with any Mirror signal — interview coverage, persona evidence, a compiled
            persona, a Mirror conversation, or likeness feedback. Lowest readiness first. Click a row
            for the per-dimension breakdown and likeness trend.
          </Typography>
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={summary}
              columns={columns}
              getRowId={(r) => r.member_id}
              loading={loading}
              onRowClick={(params) => openMember(String(params.id))}
              disableRowSelectionOnClick
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: 'readiness', sort: 'asc' }] },
              }}
              pageSizeOptions={[25, 50, 100]}
            />
          </Box>
        </>
      )}

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Local backend • {status === 'up' ? 'connected' : status}
      </Typography>
    </Box>
  );
};

export default FidelityDashboardPage;
